// Centralized configuration for all projects
// Single source of truth for project settings

import { getProjectSwitchUrl } from '../utils/environment'

// Import icons as React components for TopMenuSelectedProjects
import { ReactComponent as TaxIcon } from '../components/ProjectNavigationWindow/icons/tax-icon.svg'
import { ReactComponent as EPRIcon } from '../components/ProjectNavigationWindow/icons/epr-icon.svg'
import { ReactComponent as InvoiceIcon } from '../components/ProjectNavigationWindow/icons/invoice-icon.svg'
import { ReactComponent as AuthorizeIcon } from '../components/ProjectNavigationWindow/icons/authorize-icon.svg'

// Import icon paths for ProjectNavigationWindow (uses <img src={...} />)
import TaxIconPath from '../components/ProjectNavigationWindow/icons/tax-icon.svg'
import EPRIconPath from '../components/ProjectNavigationWindow/icons/epr-icon.svg'
import InvoiceIconPath from '../components/ProjectNavigationWindow/icons/invoice-icon.svg'
import AuthorizeIconPath from '../components/ProjectNavigationWindow/icons/authorize-icon.svg'

/**
 * Centralized projects configuration
 * Each project has:
 * - id: unique identifier (used in companyApp)
 * - name: internal name (used in code)
 * - displayName: user-facing name
 * - appId: app_id value for API/localStorage
 * - icon: SVG icon component
 * - href: URL to project (from env vars)
 * - description: project description
 * - visible: whether project is visible
 * - availableOnLocalhost: whether project appears in localhost radio buttons
 * - visibleInProjectSwitcher: whether project appears in TopMenuSelectedProjects
 */
export const PROJECTS_CONFIG = {
  LTax: {
    id: 1,
    name: 'LTax',
    displayName: "L'Tax",
    appId: 1,
    icon: TaxIcon, // React component for TopMenuSelectedProjects
    iconPath: TaxIconPath, // Path for ProjectNavigationWindow
    href: process.env.REACT_APP_LTAX_URL,
    description: 'VAT Registration・VAT Calculation・VAT reporting・VAT payment・Sales tax registration・Sales tax calculation・Sales tax reporting・Sales tax payment',
    visible: true,
    availableOnLocalhost: true,
    visibleInProjectSwitcher: true,
  },
  LEPR: {
    id: 2,
    name: 'LEPR',
    displayName: "L'EPR",
    appId: 2,
    icon: EPRIcon, // React component for TopMenuSelectedProjects
    iconPath: EPRIconPath, // Path for ProjectNavigationWindow
    href: process.env.REACT_APP_LEPR_URL,
    description: 'ERP Registration & Reporting',
    visible: true,
    availableOnLocalhost: true,
    visibleInProjectSwitcher: true,
  },
  LInvoice: {
    id: 3,
    name: 'LInvoice',
    displayName: "L'Invoice",
    appId: 3,
    icon: InvoiceIcon, // React component for TopMenuSelectedProjects
    iconPath: InvoiceIconPath, // Path for ProjectNavigationWindow
    href: process.env.REACT_APP_LINVOICE_URL,
    description: 'Send & Receive Peppol Invoices',
    visible: true,
    availableOnLocalhost: true,
    visibleInProjectSwitcher: true,
  },
  LTax_agent: {
    id: 4,
    name: 'LTax_agent',
    displayName: "L'Tax Agent",
    appId: 4,
    icon: TaxIcon, // React component for TopMenuSelectedProjects
    iconPath: TaxIconPath, // Path for ProjectNavigationWindow
    href: process.env.REACT_APP_LMARKETPLACE_URL,
    description: 'Tax Agent Services',
    visible: false, // Hidden by default
    availableOnLocalhost: true,
    visibleInProjectSwitcher: false,
  },
  LAuthorize: {
    id: 6,
    name: 'LAuthorize',
    displayName: "L'Authorize",
    appId: 6,
    icon: AuthorizeIcon, // React component for TopMenuSelectedProjects
    iconPath: AuthorizeIconPath, // Path for ProjectNavigationWindow
    href: process.env.REACT_APP_LAUTHORIZE_URL,
    description: 'CE marking・UKCA marking・GSPR label・Authorized representative services for import',
    visible: true,
    availableOnLocalhost: true,
    // visibleInProjectSwitcher: true, // Commented out: hide L'Authorize from dropdown so users cannot switch to it
    visibleInProjectSwitcher: true,
  },
}

/**
 * Get project by app_id
 * @param {number} appId - app_id value
 * @returns {object|null} project config or null
 */
export const getProjectByAppId = (appId) => {
  const appIdNum = Number(appId)
  if (!appIdNum || isNaN(appIdNum)) return null
  
  return Object.values(PROJECTS_CONFIG).find(project => project.appId === appIdNum) || null
}

/**
 * Get project by name
 * @param {string} name - project name (e.g., 'LTax', 'LEPR')
 * @returns {object|null} project config or null
 */
export const getProjectByName = (name) => {
  if (!name) return null
  return PROJECTS_CONFIG[name] || null
}

/**
 * Get all visible projects
 * @param {string} context - 'all', 'topMenu', 'switcher'
 * @returns {array} array of project configs
 */
export const getAllVisibleProjects = (context = 'all') => {
  return Object.values(PROJECTS_CONFIG).filter(project => {
    if (!project.visible) return false
    
    if (context === 'topMenu') {
      // For TopMenu radio buttons (localhost only)
      return project.availableOnLocalhost
    }
    
    if (context === 'switcher') {
      // For TopMenuSelectedProjects switcher
      return project.visibleInProjectSwitcher
    }
    
    return true
  })
}

/**
 * Get projects available for localhost (for radio buttons)
 * IMPORTANT: Returns empty array if not on localhost
 * @param {Function} isLocalhostFn - Function to check if running on localhost (to avoid circular dependency)
 * @returns {array} array of project configs
 */
export const getProjectsForLocalhost = (isLocalhostFn) => {
  // CRITICAL: Only return projects if on localhost
  if (!isLocalhostFn || !isLocalhostFn()) {
    return []
  }
  
  return getAllVisibleProjects('topMenu')
}

/**
 * Get projects list for ProjectNavigationWindow
 * @returns {array} array of project configs with id, img, label, value, description
 */
export const getProjectsListForNavigation = () => {
  return Object.values(PROJECTS_CONFIG)
    .filter(project => project.visible)
    .map(project => ({
      id: project.id,
      img: project.iconPath, // Use iconPath for <img src={...} />
      label: project.displayName,
      value: project.name,
      description: project.description,
    }))
}

/**
 * Get projects for TopMenuSelectedProjects / ProjectNavigationWindow.
 * href is resolved for current domain (lappa.org) so project switch stays on same product.
 */
export const getProjectsForSwitcher = () => {
  return getAllVisibleProjects('switcher').map(project => ({
    name: project.name,
    href: getProjectSwitchUrl(project.appId) ?? project.href,
    title: project.displayName,
    icon: project.icon,
    appId: project.appId,
  }))
}
